<template>
  <div>
    <page-content-toolbar
      :projectName="`${project && project.name ? project.name : ' '}`"
      :title="$t('option.identify')"
    />

    <div class="ma-10 pa-2" style="position: relative">
      <transition-group name="slide-up">
        <v-card
          class="px-12 pt-11 pb-8 elevation-3"
          width="100%"
          key="a"
          v-show="!optionsIdentified"
        >
          <h2 class="d-inline mr-4">{{ $t('option.create') }}: </h2>
          <v-text-field
            v-model="optionName"
            @keyup.enter="addOption"
            class="d-inline-block mr-7 rai-option-text-field"
            outlined
            color="ternary"
            :label="$t('option.name')"
          />
          <raison-button
            :button-label="$t('global.validate')"
            :button-function="addOption"
            :disabled="optionName === ''"
          />
          <br />
          <v-checkbox
            v-model="createNegation"
            class="d-inline-block"
            :label="$t('option.createNegation')"
          />
        </v-card>

        <div class="pt-6 pb-12 mb-12" key="b">
          <option-chip
            v-for="option in options"
            :key="option.id"
            :option="option"
            large
            :close="!optionsIdentified"
            :close-function="deleteOption"
            :close-function-parameters="[option]"
          />
        </div>
      </transition-group>
    </div>

    <transition-group name="fade">
      <div
        v-show="options.length && !optionsIdentified"
        class="px-12 py-9 white elevation-3 rai-footer-block"
        key="a"
      >
        <h4 class="d-inline mr-4" >
          {{ $t('option.areAllOptionsCreated') }}
        </h4>
        <raison-button
          :button-label="$t('global.yes')"
          :button-function="validateOptions"
          :loading="callingApi"
        />
      </div>
      <footer-navigation
        v-show="optionsIdentified"
        key="b"
        :prev-button-function="backToIdentifyOptions"
        prev-button-label="option.identify"
        :next-button-function="goToAddMinimalScenarii"
        next-button-label="scenario.addMinimalScenarii"
      />
    </transition-group>

    <delete-option-dialog
      :open="openDeleteDialog"
      :option="optionToDelete"
      @cancel-option-deletion="openDeleteDialog = false"
      @option-deleted="onOptionDeleted"
    />

  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import RaisonButton from '@/components/global/RaisonButton';
import FooterNavigation from '@/components/layout/FooterNavigation';
import OptionChip from '@/components/global/OptionChip';
import DeleteOptionDialog from '@/components/global/DeleteOptionDialog';

import projectPageMixin from '@/mixins/project-page-mixin.js';

import optionService from '@/services/options.js';

export default {
  name: 'IdentifyOptions',

  components: { PageContentToolbar, RaisonButton, FooterNavigation, OptionChip, DeleteOptionDialog },

  mixins: [ projectPageMixin ],

  data () {
    return {
      optionName: '',
      options: [],
      createNegation: false,
      optionsIdentified: false,
      callingApi: false,
      openDeleteDialog: false,
      optionToDelete: {},
    };
  },

  computed: {
    showLogicalLanguage () {
      return this.$store.state.showLogicalLangage;
    },
  },

  methods: {

    onOptionDeleted(optionDeletedEvent) {
      const deletedOptionId = optionDeletedEvent.optionId;
      this.options.splice(this.options.findIndex(option => option.id === deletedOptionId), 1);
      this.$emit('raison-event', { type: 'success', messages: [this.$t('option.successDeletion', {optionName: this.optionToDelete.name})] });

      this.openDeleteDialog = false;
      this.optionToDelete = {};
    },

    addOption () {
      if (this.optionName !== '') {
        let option = {
          "name": this.showLogicalLanguage ? '' : this.optionName,
          "prolog": this.showLogicalLanguage ? this.optionName : '',
          "loading": true,
        };

        let negationOption;
        if (this.createNegation) {
          negationOption = {
            "name": `${this.showLogicalLanguage ? '' : 'not ' + this.optionName}`,
            "prolog": `${this.showLogicalLanguage ? 'neg(' + this.optionName + ')' : ''}`,
            "loading": true,
          };
        }

        let p1 = this.createOption(option);
        if (negationOption) {
          let p2 = this.createOption(negationOption);

          Promise.all([p1, p2]).then(values => {
            const conflictingOptions = {
              "option": values[0],
              "optioncomplement": values[1],
            };

            optionService.createConflictingOptions(this.$route.params.id, conflictingOptions).then(() => {
              this.$emit('raison-event', { type: 'success', messages: [this.$t('option.contradictoryCreated')]});
            }).catch(err => {
              this.$emit('raison-event', { type: 'error', messages: [err]});
            })
          })
        }
      }
    },

    createOption (option) {
      return new Promise((resolve, reject) => {
        this.options.push(option);

        optionService.createOption(this.$route.params.id, option).then(res => {
          const idx = this.options.indexOf(option);
          if (idx > -1) {
            this.options.splice(idx, 1);
          }
          this.options.push(res.data);
          this.optionName = '';

          resolve(res.data);
        }).catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
          const idx = this.options.indexOf(option);
          if (idx > -1) {
            this.options.splice(idx, 1);
          }

          reject();
        });
      })
    },

    deleteOption (option) {
      this.optionToDelete = option;
      this.openDeleteDialog = true;
    },

    validateOptions () {
      this.optionsIdentified = true;
      this.$emit('raison-event', { type: 'success', messages: [this.$t('option.created')] });
    },

    backToIdentifyOptions () {
      this.optionsIdentified = false;
    },

    goToAddMinimalScenarii () {
      this.$router.push({name: 'addScenarii', params: { id: this.$route.params.id } });
    },

    loadOptions () {
      optionService.getOptions(this.$route.params.id).then(res => {
        this.options = res.data;
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] });
      })
    },
  },

  created () {
    this.loadOptions();
  },
};
</script>

<style>
.rai-option-text-field {
  width: 350px;
}

.rai-option-text-field .v-input__control {
  margin-bottom: -19px;
}

.rai-footer-block {
  position: absolute;
  bottom: 0px;
  right: 30px;
  left: 30px;
}

.rai-footer-block__full {
  position: absolute;
  bottom: 0px;
  right: 0px;
  left: 0px;
}

/* Fade animation */
.fade-enter-active, .fade-leave-active {
  transition: opacity .4s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* ----------------------------------------------
 * Generated by Animista on 2020-7-8 15:44:9
 * ---------------------------------------------- */
/* Slide-up animation */
@-webkit-keyframes slide-up {
  0% {
    -webkit-transform: translateY(0);
            transform: translateY(0);
    opacity: 1;
  }
  100% {
    -webkit-transform: translateY(-150px);
            transform: translateY(-150px);
    opacity: 0;
  }
}
@keyframes slide-up {
  0% {
    -webkit-transform: translateY(0);
            transform: translateY(0);
    opacity: 1;
  }
  100% {
    -webkit-transform: translateY(-150px);
            transform: translateY(-150px);
    opacity: 0;
  }
}

.slide-up-leave-active {
  animation: slide-up .4s;
  position: absolute;
}

.slide-up-move {
  transition: all .4s;
}
</style>